/**
 * Custom hook for fetching financial table data
 * 
 * Provides data fetching, loading states, error handling for HierarchicalFinancialTable
 * Supports both current and snapshot data with auto-refresh capabilities
 * 
 * @author Financial Table Team
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import financialTableService from '../services/financialTableService';

export const useFinancialTableData = (options = {}) => {
    const {
        year,
        month,
        brandId,
        campaignId,
        mediaType,
        autoRefresh = false,
        refreshInterval = 5 * 60 * 1000, // 5 minutes
        useCache = true,
        isSnapshotMode = false,
        snapshotDate = null
    } = options;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [meta, setMeta] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    const refreshIntervalRef = useRef(null);
    const abortControllerRef = useRef(null);

    /**
     * Fetch financial data from API
     */
    const fetchData = useCallback(async (forceRefresh = false) => {
        // Cancel previous request if still pending
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
            setLoading(true);
            setError(null);

            const fetchOptions = {
                year,
                month,
                brandId,
                campaignId,
                mediaType,
                useCache,
                forceRefresh,
                signal: abortControllerRef.current.signal,
                isSnapshotMode,
                snapshotDate
            };

            const response = await financialTableService.fetchFinancialData(fetchOptions);

            // Check if request was aborted
            if (abortControllerRef.current.signal.aborted) {
                return;
            }

            setData(response.data || []);
            setMeta(response.meta || null);
            setLastFetched(new Date());
            setError(null);
            
            // Remove debug logging to prevent infinite loop

        } catch (err) {
            // Don't set error if request was aborted
            if (err.name === 'AbortError') {
                return;
            }

            console.error('Failed to fetch financial data:', err);
            
            const errorMessage = err.response?.data?.error || 
                               err.message || 
                               'Failed to load financial data';
            
            setError({
                message: errorMessage,
                status: err.response?.status,
                timestamp: new Date()
            });

            // Keep existing data on error (don't clear it)
            // setData([]);
        } finally {
            if (!abortControllerRef.current?.signal.aborted) {
                setLoading(false);
            }
        }
    }, [year, month, brandId, campaignId, mediaType, useCache, isSnapshotMode, snapshotDate]);

    /**
     * Force refresh data (bypass cache)
     */
    const refreshData = useCallback(() => {
        return fetchData(true);
    }, [fetchData]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Setup auto-refresh interval
     */
    const setupAutoRefresh = useCallback(() => {
        if (autoRefresh && refreshInterval > 0) {
            refreshIntervalRef.current = setInterval(() => {
                fetchData(false); // Use cache for auto-refresh
            }, refreshInterval);
        }
    }, [autoRefresh, refreshInterval]); // Remove fetchData dependency

    /**
     * Clear auto-refresh interval
     */
    const clearAutoRefresh = useCallback(() => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
    }, []);

    /**
     * Initial data fetch and setup auto-refresh - optimized
     */
    useEffect(() => {
        // Only fetch if we don't have data or if key parameters changed
        const hasData = data && data.length > 0;
        const shouldFetch = !hasData || 
                           (year && month && brandId && campaignId && mediaType) ||
                           isSnapshotMode || 
                           snapshotDate;
        
        if (shouldFetch) {
            fetchData();
        }
        
        setupAutoRefresh();

        return () => {
            clearAutoRefresh();
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [year, month, brandId, campaignId, mediaType, isSnapshotMode, snapshotDate]); // Remove fetchData dependency

    /**
     * Update auto-refresh when settings change
     */
    useEffect(() => {
        clearAutoRefresh();
        setupAutoRefresh();

        return () => {
            clearAutoRefresh();
        };
    }, [autoRefresh, refreshInterval]); // Remove function dependencies

    /**
     * Calculate data freshness
     */
    const getDataFreshness = useCallback(() => {
        if (!lastFetched) return null;

        const now = new Date();
        const diffMs = now - lastFetched;
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffMinutes < 1) return 'Just now';
        if (diffMinutes === 1) return '1 minute ago';
        if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
        
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours === 1) return '1 hour ago';
        if (diffHours < 24) return `${diffHours} hours ago`;
        
        return lastFetched.toLocaleDateString();
    }, [lastFetched]);

    /**
     * Get current month information
     */
    const getMonthInfo = useCallback(() => {
        return financialTableService.getCurrentMonthParams();
    }, []);

    return {
        // Data states
        data,
        loading,
        error,
        meta,
        lastFetched,

        // Actions
        fetchData,
        refreshData,
        clearError,

        // Utilities
        getDataFreshness,
        getMonthInfo,

        // Stats
        isEmpty: !loading && (!data || data.length === 0),
        hasData: !loading && data && data.length > 0,
        isStale: lastFetched && (new Date() - lastFetched) > refreshInterval,

        // Cache controls
        clearCache: financialTableService.clearCache.bind(financialTableService),
        getCacheStats: financialTableService.getCacheStats.bind(financialTableService)
    };
};

export default useFinancialTableData;